import { useEffect, useRef, useState } from 'react';
import axios, { AxiosError } from 'axios';
import { QuoteService } from 'services/quote.service';
import {
  EQuoteSlaFilterOptions,
  EQuoteStatus,
  IOutputQuoteListFilters,
  IQuote,
  IQuoteFilters,
  IQuoteListByStatusResponse,
  IQuotesAdditionalCalcs,
  TQuotesCountByStatus,
  TQuoteTypes
} from 'types/quote.types';
import { displayErrorNotification } from 'utils/displayNudosStandardNotifications';
import { PAGINATION_INITIAL_PAGE } from 'constants/constants';
import { formatQuote } from '../QuoteScreen/utils/common';

const NAMES_BY_QUOTE_TYPE: Record<TQuoteTypes, string> = {
  REGULAR: 'Regular',
  EXPLORATORY: 'Exploratorio',
  DUPLICATE: 'Duplicado'
};

const NAMES_OF_SLA_OPTIONS: Record<EQuoteSlaFilterOptions, string> = {
  on_time: 'A tiempo',
  due_soon: 'Por vencer',
  expired: 'Vencido'
};

const formatFilterQueryParam = (value?: string[]) => (value?.length ? value?.join(',') : undefined);

const isAbortedRequest = (error: unknown): boolean =>
  axios.isCancel(error) || (axios.isAxiosError(error) && (error as AxiosError).code === 'ERR_CANCELED');

const formatFilters = (filters: IQuoteFilters) => {
  return {
    ...filters,
    page: filters?.page || PAGINATION_INITIAL_PAGE,
    search: filters?.search || undefined,
    organizationIds: formatFilterQueryParam(filters?.organizationIds),
    quoteTypeIds: formatFilterQueryParam(filters?.quoteTypeIds),
    responsibleUserIds: formatFilterQueryParam(filters?.userIds),
    slaRules: formatFilterQueryParam(filters?.slaRules),
    sortData: formatFilterQueryParam(filters?.sortData),
    sortValues: formatFilterQueryParam(filters?.sortValues),
    exchangeIds: formatFilterQueryParam(filters?.countryIds),
    userIds: undefined
  };
};

export const useQuotes = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [quotes, setQuotes] = useState<IQuote[]>([]);
  const [filterOptions, setFilterOptions] = useState<IOutputQuoteListFilters>();
  const [countByStatus, setCountByStatus] = useState<TQuotesCountByStatus>({
    EXPIRED: 0,
    PENDING_APPROVAL: 0,
    PURCHASED: 0,
    TO_QUOTE: 0,
    REJECTED: 0
  });
  const [additionalCalcs, setAdditionalCalcs] = useState<IQuotesAdditionalCalcs>({
    expiredPendingQuotesCount: 0,
    withSLACTO: false
  });

  const listAbortControllerRef = useRef<AbortController | null>(null);
  const listRequestIdRef = useRef(0);

  /** Cancela la petición de listado en curso al desmontar el hook. */
  useEffect(() => {
    return () => listAbortControllerRef.current?.abort();
  }, []);

  const getAllByStatus = (status: EQuoteStatus, filters: IQuoteFilters): Promise<IQuoteListByStatusResponse> => {
    listAbortControllerRef.current?.abort();

    const controller = new AbortController();
    listAbortControllerRef.current = controller;
    const requestId = ++listRequestIdRef.current;

    return new Promise((resolve, reject) => {
      setIsLoading(true);

      QuoteService.getAllByStatus(status, formatFilters(filters), { signal: controller.signal })
        .then(res => {
          if (requestId !== listRequestIdRef.current) return;

          const quotes = res?.quotes?.map(formatQuote);
          const page = filters?.page ?? PAGINATION_INITIAL_PAGE;
          const accumulate = page > PAGINATION_INITIAL_PAGE;
          setQuotes(prev => (accumulate ? [...prev, ...quotes] : quotes));
          resolve(res);
        })
        .catch(error => {
          if (isAbortedRequest(error)) {
            reject(error);
            return;
          }

          if (requestId !== listRequestIdRef.current) return;

          reject(error);
          displayErrorNotification();
        })
        .finally(() => {
          if (requestId === listRequestIdRef.current) {
            setIsLoading(false);
          }
        });
    });
  };

  const getCountsByStatus = (filters: IQuoteFilters) => {
    QuoteService.getCountsByStatus(formatFilters(filters))
      .then(res => {
        setCountByStatus(res.quoteCountByStatus);
        setAdditionalCalcs({
          expiredPendingQuotesCount: res.expiredPendingQuotesCount,
          withSLACTO: res.withSLACTO
        });
      })
      .catch(() => {
        displayErrorNotification();
      });
  };

  const getFilters = () => {
    QuoteService.getFilters()
      .then(res => {
        setFilterOptions({
          ...res,
          organizations: res?.organizations?.map(o => ({ ...o, name: `${o?.name ?? ''} - ${o.id ?? ''}` })),
          quotesTypes: res?.quotesTypes?.map(q => ({
            ...q,
            name: q?.code ? NAMES_BY_QUOTE_TYPE[q.code as TQuoteTypes] : ''
          })),
          quoteSla: res.quoteSla?.map(q => ({
            ...q,
            name: q?.code ? NAMES_OF_SLA_OPTIONS[q.code as EQuoteSlaFilterOptions] : ''
          }))
        });
      })
      .catch(() => {
        displayErrorNotification();
      });
  };

  const clearQuotes = () => {
    setQuotes([]);
  };

  return {
    additionalCalcs,
    filterOptions,
    quotes,
    countByStatus,
    isLoading,
    getAllByStatus,
    getFilters,
    getCountsByStatus,
    clearQuotes
  };
};
